import type { SlickGrid } from "@slickgrid-universal/common";

/**
 * Safe wrappers around SlickGrid's row-selection API.
 *
 * `getSelectedRows` and `setSelectedRows` each open with
 * `if (!this.selectionModel) { throw new Error('SlickGrid Selection model is not set'); }`.
 * This is NOT new in SlickGrid 10 — verified in the version this branch ships,
 * @slickgrid-universal/common 5.7.0, at dist/esm/core/slickGrid.js:5423 (`getSelectedRows`) and
 * :5434 (`setSelectedRows`), with the same `getSelectionModel(): SelectionModel | undefined`
 * accessor available to test for it (dist/types/core/slickGrid.d.ts:351).
 *
 * A model is registered only when one of `enableSelection`, `enableCheckboxSelector`,
 * `enableRowDetailView` or `enableRowMoveManager` is on, and only once the extension service has
 * run. So there is both a configuration case (both wrappers set `enableSelection: !props.isEditMode`,
 * so every edit-mode grid has no model) and a lifecycle case (any call that lands before extensions
 * register, or after the grid is disposed).
 *
 * The damage was never the failed selection call itself — it is that an unguarded throw escapes the
 * function it sits in. In both wrappers' data-load path it aborts the load a couple of lines above
 * the `setGridReady` call, so the grid stays mounted but never announces itself and renders as
 * blank rows. That is the reported TreeView / TreeDropDown / template-picker symptom, and it is
 * what Sentry CUBE-FRONTEND-E3E and CUBE-FRONTEND-ECB capture:
 *
 *     SlickGrid/index.tsx  loadedGrid.setSelectedRows([])
 *       -> slickGrid.js    throw new Error('SlickGrid Selection model is not set')
 *
 * Testing `getSelectionModel()` is what makes this safe; testing that the METHOD exists (as the
 * call site here previously did, `if (grid?.setSelectedRows)`) does not, because the method is
 * always present and throws from inside.
 *
 * Clearing or reading a selection is best-effort housekeeping, so these helpers no-op on a grid with
 * no model and behave exactly like the raw calls on a grid that has one.
 */

/**
 * Only the three selection methods, so both wrappers can pass their differently-parameterised grid
 * instances without threading a row type through — none of the three signatures mentions `TData`.
 */
type GridSelectionApi = Pick<SlickGrid, "getSelectionModel" | "getSelectedRows" | "setSelectedRows">;

/** The selected row indices, or `[]` when the grid has no selection model to answer with. */
export const getSelectedRowsSafe = (grid: GridSelectionApi | null | undefined): number[] => {
  if (!grid?.getSelectionModel()) {
    return [];
  }
  return grid.getSelectedRows();
};

/** Applies a row selection, or does nothing when the grid has no selection model. */
export const setSelectedRowsSafe = (
  grid: GridSelectionApi | null | undefined,
  rows: number[],
  caller?: string,
): void => {
  if (!grid?.getSelectionModel()) {
    return;
  }
  grid.setSelectedRows(rows, caller);
};
